package se.mittpass.web.components

import se.mittpass.web.*
import se.mittpass.web.components.*
import se.mittpass.web.pages.*
import se.mittpass.web.viewmodel.*
import se.mittpass.web.style.*
import se.mittpass.web.pages.fortnoxRepo


import androidx.compose.runtime.*
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*
import se.mittpass.shared.api.PayrollSummaryDto

private val summaryScope = MainScope()

@Composable
fun PayrollSummarySection(
    summaries: List<PayrollSummaryDto>,
    workplaceId: Long,
    rangeFrom: String,
    rangeTo: String,
    isMobile: Boolean = false,
    onReload: () -> Unit = {}
) {
    val sorted = summaries.sortedBy {
        listOfNotNull(it.firstName, it.lastName).joinToString(" ").ifEmpty { it.userEmail }
    }
    var exporting by remember { mutableStateOf(false) }
    var showInactive by remember { mutableStateOf(false) }

    // Existing payslips for this workplace & period
    var existingPayslips by remember { mutableStateOf<List<se.mittpass.shared.api.PayslipDto>>(emptyList()) }
    var loadingPayslips by remember { mutableStateOf(false) }

    fun refreshPayslips() {
        loadingPayslips = true
        summaryScope.launch {
            payrollRepo.getPayslips(workplaceId).onSuccess { list ->
                existingPayslips = list.filter { it.periodStart == rangeFrom && it.periodEnd == rangeTo }
                loadingPayslips = false
            }.onFailure {
                loadingPayslips = false
            }
        }
    }

    LaunchedEffect(workplaceId, rangeFrom, rangeTo) {
        refreshPayslips()
    }

    // Salary edit modal state
    var editingSalaryMember by remember { mutableStateOf<se.mittpass.shared.api.StaffMemberDto?>(null) }
    var staffList by remember { mutableStateOf<List<se.mittpass.shared.api.StaffMemberDto>>(emptyList()) }
    var caSettings by remember { mutableStateOf<se.mittpass.shared.api.CollectiveAgreementDto?>(null) }

    LaunchedEffect(workplaceId) {
        staffRepo.getStaff(workplaceId).onSuccess { staffList = it }
        collectiveAgreementRepo.getSettings(workplaceId).onSuccess { caSettings = it }
    }

    fun openSalaryModalForUser(userId: Long) {
        val member = staffList.find { it.userId == userId } ?: run {
            val s = summaries.find { it.userId == userId }
            if (s != null) {
                se.mittpass.shared.api.StaffMemberDto(
                    userId = s.userId,
                    email = s.userEmail,
                    role = "staff",
                    firstName = s.firstName,
                    lastName = s.lastName,
                    hourlyRateOre = s.hourlyRateOre,
                    monthlySalaryOre = s.monthlySalaryOre,
                    taxTable = s.taxTable,
                    taxRatePct = s.taxRatePct,
                    obType = s.obType,
                    annualVacationDays = s.vacationDaysTotal
                )
            } else null
        }
        editingSalaryMember = member
    }

    // Split into with-payout vs no-payout (now also includes vacation & sick pay!)
    val (activeList, inactiveList) = remember(sorted) {
        sorted.partition {
            val totalMin = it.normalMinutes + it.obEveningMinutes + it.obNightMinutes + it.obWeekendMinutes
            totalMin > 0 || it.vacationMinutes > 0 || it.sickMinutes > 0 || it.vabMinutes > 0 || it.leaveMinutes > 0 || it.totalSek > 0 || it.monthlySalaryOre != null
        }
    }

    // Employees with worked hours or vacation/sick leave who lack an hourly rate or monthly salary
    val missingSalaryList = remember(activeList) {
        activeList.filter { it.hourlyRateOre == null && it.monthlySalaryOre == null }
    }

    Div({
        style {
            property("background", "var(--rp-surface)"); property("border-radius", "16px")
            property("border", "1px solid var(--rp-hl-med)"); property("overflow", "hidden")
            property("margin-bottom", "0.75rem")
        }
    }) {
        // ── Header ──
        Div({
            style {
                property("display", "flex"); property("align-items", "center")
                property("justify-content", "space-between"); property("padding", "0.875rem 1.25rem")
                property("border-bottom", "1px solid var(--rp-hl-med)")
            }
        }) {
            Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.6rem"); property("flex-wrap", "wrap") } }) {
                Span({ style { property("font-weight", "800"); property("font-size", "0.95rem"); property("color", "var(--rp-text)") } }) {
                    Text("Lönesammanställning")
                }
                Span({ style {
                    property("background", "var(--rp-info-bg)"); property("color", "var(--rp-pine)")
                    property("padding", "0.15rem 0.55rem"); property("border-radius", "20px")
                    property("font-weight", "700"); property("font-size", "0.75rem")
                } }) { Text("${activeList.size} att utbetala") }

                if (missingSalaryList.isNotEmpty()) {
                    Span({ style {
                        property("background", "var(--rp-err-bg)"); property("color", "var(--rp-love)")
                        property("border", "1px solid var(--rp-err-border)")
                        property("padding", "0.15rem 0.6rem"); property("border-radius", "20px")
                        property("font-weight", "700"); property("font-size", "0.75rem")
                        property("display", "flex"); property("align-items", "center"); property("gap", "0.35rem")
                    } }) {
                        FaIcon("fa-solid fa-triangle-exclamation", size = "0.7rem", color = "var(--rp-err-text)")
                        Text("${missingSalaryList.size} saknar lön/timpeng")
                    }
                }
            }
        }

        // ── Warning banner when employees have hours but missing salary ──
        if (missingSalaryList.isNotEmpty()) {
            val names = missingSalaryList.take(3).joinToString(", ") {
                listOfNotNull(it.firstName, it.lastName).joinToString(" ").ifEmpty { it.userEmail }
            } + if (missingSalaryList.size > 3) " m.fl." else ""

            Div({
                style {
                    property("background", "var(--rp-warn-bg)")
                    property("border-bottom", "1px solid var(--rp-warn-border)")
                    property("padding", "0.75rem 1.25rem")
                    property("display", "flex")
                    property("align-items", "center")
                    property("gap", "0.75rem")
                }
            }) {
                Div({
                    style {
                        property("width", "30px"); property("height", "30px")
                        property("border-radius", "8px"); property("background", "var(--rp-warn-bg)")
                        property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
                        property("flex-shrink", "0")
                    }
                }) {
                    FaIcon("fa-solid fa-triangle-exclamation", size = "0.9rem", color = "var(--rp-warn-text)")
                }
                Div({ style { property("flex", "1") } }) {
                    Div({ style { property("font-weight", "800"); property("font-size", "0.84rem"); property("color", "var(--rp-warn-text)") } }) {
                        Text(if (missingSalaryList.size == 1) "1 anställd saknar angiven lön/timpeng" else "${missingSalaryList.size} anställda saknar angiven lön/timpeng")
                    }
                    Div({ style { property("font-size", "0.76rem"); property("color", "var(--rp-warn-text)"); property("margin-top", "0.1rem") } }) {
                        Text("$names har registrerad arbetstid eller semester men saknar timpeng eller månadslön under Personal. Beräknad lön kan inte fastställas.")
                    }
                }
                Button({
                    onClick {
                        missingSalaryList.firstOrNull()?.let { openSalaryModalForUser(it.userId) }
                    }
                    style {
                        property("padding", "0.45rem 0.85rem")
                        property("background", "var(--rp-gold)"); property("color", "white")
                        property("border", "none"); property("border-radius", "8px")
                        property("font-size", "0.78rem"); property("font-weight", "700")
                        property("cursor", "pointer"); property("white-space", "nowrap")
                        property("display", "inline-flex"); property("align-items", "center"); property("gap", "0.35rem")
                    }
                }) {
                    FaIcon("fa-solid fa-coins", size = "0.75rem", color = "white")
                    Text("Sätt lön direkt")
                }
            }
        }



        val totalGrossSek = activeList.sumOf { it.grossPaySek.takeIf { g -> g > 0.0 } ?: it.totalSek }
        val totalBaseSek = activeList.sumOf { it.basePaySek }
        val totalObSek = activeList.sumOf { it.obSurchargeSek }
        val totalVacationSek = activeList.sumOf { it.vacationPaySek }
        val totalSickSek = activeList.sumOf { it.sickPaySek }
        val totalTaxSek = activeList.sumOf { it.taxDeductionSek.takeIf { t -> t > 0.0 } ?: (it.totalSek * 0.30) }
        val totalNetSek = activeList.sumOf { it.netPaySek.takeIf { n -> n > 0.0 } ?: (it.totalSek * 0.70) }
        val totalEmployerContributionSek = activeList.sumOf { it.employerContributionSek.takeIf { e -> e > 0.0 } ?: (it.totalSek * 0.3142) }
        val totalCostSek = activeList.sumOf { it.totalCostSek.takeIf { c -> c > 0.0 } ?: (it.totalSek * 1.3142) }
        val totalWorkedMins = activeList.sumOf { it.normalMinutes + it.obEveningMinutes + it.obNightMinutes + it.obWeekendMinutes }
        val totalVacationMins = activeList.sumOf { it.vacationMinutes }
        val totalSickMins = activeList.sumOf { it.sickMinutes }
        val totalVabMins = activeList.sumOf { it.vabMinutes }
        val totalLeaveMins = activeList.sumOf { it.leaveMinutes }

        var generatingPayslips by remember { mutableStateOf(false) }
        var payslipsFeedback by remember { mutableStateOf<String?>(null) }

        // Default payment date calculation based on caSettings or rangeTo
        val defaultPaydayDay = caSettings?.paydayDayOfMonth ?: 25
        var customPayDate by remember(rangeTo, defaultPaydayDay) {
            val parts = rangeTo.split("-")
            val pDate = if (parts.size == 3) {
                val year = parts[0]
                val month = parts[1]
                val dayStr = defaultPaydayDay.toString().padStart(2, '0')
                "$year-$month-$dayStr"
            } else rangeTo
            mutableStateOf(pDate)
        }

        // Action header row with button for generating payslips
        Div({
            style {
                property("display", "flex"); property("align-items", "center")
                property("justify-content", "space-between"); property("padding", "0.6rem 1.25rem")
                property("background", "var(--rp-surface)"); property("border-bottom", "1px solid var(--rp-overlay)")
                property("flex-wrap", "wrap"); property("gap", "0.5rem")
            }
        }) {
            Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.75rem"); property("flex-wrap", "wrap") } }) {
                Div({ style { property("font-size", "0.82rem"); property("color", "var(--rp-subtle)"); property("font-weight", "600") } }) {
                    Text("Period: $rangeFrom – $rangeTo")
                }
                Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.35rem") } }) {
                    Span({ style { property("font-size", "0.75rem"); property("color", "var(--rp-subtle)"); property("font-weight", "700") } }) {
                        Text("Utbetalningsdag:")
                    }
                    Input(InputType.Date) {
                        value(customPayDate)
                        onInput { customPayDate = it.value }
                        style {
                            property("padding", "0.2rem 0.5rem"); property("border", "1px solid var(--rp-hl-med)")
                            property("border-radius", "6px"); property("font-size", "0.78rem"); property("font-weight", "600")
                            property("color", "var(--rp-text)")
                        }
                    }
                }
            }
            val draftCount = existingPayslips.count { it.status == "draft" }
            val publishedCount = existingPayslips.count { it.status == "published" }

            Div({ style { property("display", "flex"); property("gap", "0.6rem"); property("align-items", "center"); property("flex-wrap", "wrap") } }) {
                payslipsFeedback?.let { fb ->
                    Span({ style { property("font-size", "0.8rem"); property("font-weight", "700"); property("color", "var(--rp-ok-text)") } }) {
                        Text(fb)
                    }
                }

                // Batch draft generation button
                Button({
                    onClick {
                        generatingPayslips = true
                        summaryScope.launch {
                            val payDate = customPayDate.ifBlank { rangeTo }
                            val req = se.mittpass.shared.api.GeneratePayslipsRequest(
                                periodStart = rangeFrom,
                                periodEnd = rangeTo,
                                paymentDate = payDate,
                                autoPublish = false
                            )
                            payrollRepo.generatePayslips(workplaceId, req).onSuccess {
                                payslipsFeedback = "✓ Utkast genererade för granskning!"
                                refreshPayslips()
                            }.onFailure {
                                payslipsFeedback = "Ett fel uppstod vid generering"
                            }
                            generatingPayslips = false
                        }
                    }
                    style {
                        property("padding", "0.45rem 0.9rem"); property("border", "1px solid var(--rp-hl-med)")
                        property("background", "var(--rp-surface)"); property("color", "var(--rp-pine)")
                        property("border-radius", "8px"); property("font-weight", "700"); property("font-size", "0.8rem")
                        property("cursor", "pointer"); property("display", "flex"); property("align-items", "center"); property("gap", "0.4rem")
                        property("box-shadow", "0 1px 3px rgba(0, 0, 0, 0.05)")
                    }
                }) {
                    FaIcon("fa-solid fa-file-invoice", size = "0.8rem", color = "var(--rp-subtle)")
                    Text(if (generatingPayslips) "Genererar…" else "Generera utkast (alla)")
                }

                // Publish all draft payslips button
                if (draftCount > 0) {
                    var publishingAll by remember { mutableStateOf(false) }
                    Button({
                        onClick {
                            publishingAll = true
                            summaryScope.launch {
                                payrollRepo.publishAllPayslips(workplaceId).onSuccess {
                                    payslipsFeedback = "✓ $draftCount lönebesked publicerade till personal!"
                                    refreshPayslips()
                                }.onFailure {
                                    payslipsFeedback = "Kunde inte publicera"
                                }
                                publishingAll = false
                            }
                        }
                        style {
                            property("padding", "0.45rem 0.9rem"); property("border", "none")
                            property("background", "linear-gradient(135deg, var(--rp-pine), var(--rp-foam))"); property("color", "white")
                            property("border-radius", "8px"); property("font-weight", "700"); property("font-size", "0.8rem")
                            property("cursor", "pointer"); property("display", "flex"); property("align-items", "center"); property("gap", "0.4rem")
                            property("box-shadow", "0 2px 6px rgba(0, 184, 151, 0.2)")
                        }
                    }) {
                        FaIcon("fa-solid fa-paper-plane", size = "0.8rem", color = "white")
                        Text(if (publishingAll) "Publicerar…" else "Publicera alla utkast ($draftCount)")
                    }
                }
            }
        }

        // ── KPI Summary Cards ──
        Div({
            style {
                property("display", "grid")
                property("grid-template-columns", if (isMobile) "repeat(2, 1fr)" else "repeat(auto-fit, minmax(160px, 1fr))")
                property("gap", "0.75rem"); property("padding", "0.875rem 1.25rem")
                property("background", "var(--rp-overlay)"); property("border-bottom", "1px solid var(--rp-overlay)")
            }
        }) {
            // Card 1: Total bruttolön
            Div({
                style {
                    property("background", "var(--rp-surface)"); property("padding", "0.75rem 1rem")
                    property("border-radius", "12px"); property("border", "1px solid var(--rp-hl-med)")
                }
            }) {
                Span({ style { property("font-size", "0.7rem"); property("font-weight", "700"); property("color", "var(--rp-subtle)"); property("text-transform", "uppercase") } }) {
                    Text("Total bruttolön")
                }
                Div({ style { property("font-size", "1.2rem"); property("font-weight", "800"); property("color", "var(--rp-text)"); property("margin-top", "0.15rem") } }) {
                    Text("${totalGrossSek.toLong()} kr")
                }
            }

            // Card 1b: Preliminärskatt
            Div({
                style {
                    property("background", "var(--rp-surface)"); property("padding", "0.75rem 1rem")
                    property("border-radius", "12px"); property("border", "1px solid var(--rp-hl-med)")
                }
            }) {
                Span({ style { property("font-size", "0.7rem"); property("font-weight", "700"); property("color", "var(--rp-err-text)"); property("text-transform", "uppercase") } }) {
                    Text("Prel. skatt (avdrag)")
                }
                Div({ style { property("font-size", "1.2rem"); property("font-weight", "800"); property("color", "var(--rp-err-text)"); property("margin-top", "0.15rem") } }) {
                    Text("-${totalTaxSek.toLong()} kr")
                }
            }

            // Card 1c: Uppskattad nettolön
            Div({
                style {
                    property("background", "var(--rp-surface)"); property("padding", "0.75rem 1rem")
                    property("border-radius", "12px"); property("border", "1px solid var(--rp-hl-med)")
                }
            }) {
                Span({ style { property("font-size", "0.7rem"); property("font-weight", "700"); property("color", "var(--rp-ok-text)"); property("text-transform", "uppercase") } }) {
                    Text("Att utbetala (netto)")
                }
                Div({ style { property("font-size", "1.2rem"); property("font-weight", "800"); property("color", "var(--rp-ok-text)"); property("margin-top", "0.15rem") } }) {
                    Text("${totalNetSek.toLong()} kr")
                }
            }

            // Card 2: Arbetsgivaravgift (31,42%)
            Div({
                style {
                    property("background", "var(--rp-surface)"); property("padding", "0.75rem 1rem")
                    property("border-radius", "12px"); property("border", "1px solid var(--rp-hl-med)")
                }
            }) {
                Span({ style { property("font-size", "0.7rem"); property("font-weight", "700"); property("color", "var(--rp-pine)"); property("text-transform", "uppercase") } }) {
                    Text("Arb.avgift (31,42%)")
                }
                Div({ style { property("font-size", "1.2rem"); property("font-weight", "800"); property("color", "var(--rp-pine)"); property("margin-top", "0.15rem") } }) {
                    Text("${totalEmployerContributionSek.toLong()} kr")
                }
            }

            // Card 3: Total personalkostnad (Företaget betalar)
            Div({
                style {
                    property("background", "var(--rp-info-bg)"); property("padding", "0.75rem 1rem")
                    property("border-radius", "12px"); property("border", "1px solid var(--rp-info-border)")
                }
            }) {
                Span({ style { property("font-size", "0.7rem"); property("font-weight", "800"); property("color", "var(--rp-pine)"); property("text-transform", "uppercase") } }) {
                    Text("Total kostnad (företag)")
                }
                Div({ style { property("font-size", "1.25rem"); property("font-weight", "900"); property("color", "var(--rp-pine)"); property("margin-top", "0.15rem") } }) {
                    Text("${totalCostSek.toLong()} kr")
                }
            }

            // Card 4: OB-ersättning
            Div({
                style {
                    property("background", "var(--rp-surface)"); property("padding", "0.75rem 1rem")
                    property("border-radius", "12px"); property("border", "1px solid var(--rp-hl-med)")
                }
            }) {
                Span({ style { property("font-size", "0.7rem"); property("font-weight", "700"); property("color", "var(--rp-warn-text)"); property("text-transform", "uppercase") } }) {
                    Text("Varav OB")
                }
                Div({ style { property("font-size", "1.2rem"); property("font-weight", "800"); property("color", "var(--rp-warn-text)"); property("margin-top", "0.15rem") } }) {
                    Text("${totalObSek.toLong()} kr")
                }
            }

            // Card 5: Semesterersättning (12%)
            if (totalVacationSek > 0 || totalVacationMins > 0) {
                Div({
                    style {
                        property("background", "var(--rp-surface)"); property("padding", "0.75rem 1rem")
                        property("border-radius", "12px"); property("border", "1px solid var(--rp-hl-med)")
                    }
                }) {
                    Span({ style { property("font-size", "0.7rem"); property("font-weight", "700"); property("color", "var(--rp-ok-text)"); property("text-transform", "uppercase") } }) {
                        Text("Semesterers. (12%)")
                    }
                    Div({ style { property("font-size", "1.2rem"); property("font-weight", "800"); property("color", "var(--rp-ok-text)"); property("margin-top", "0.15rem") } }) {
                        Text("${totalVacationSek.toLong()} kr")
                    }
                }
            }

            // Card 5b: Sjuklön (80% efter karensdag)
            if (totalSickSek > 0 || totalSickMins > 0) {
                Div({
                    style {
                        property("background", "var(--rp-surface)"); property("padding", "0.75rem 1rem")
                        property("border-radius", "12px"); property("border", "1px solid var(--rp-hl-med)")
                    }
                }) {
                    Span({ style { property("font-size", "0.7rem"); property("font-weight", "700"); property("color", "var(--rp-err-text)"); property("text-transform", "uppercase") } }) {
                        Text("Sjuklön (80%)")
                    }
                    Div({ style { property("font-size", "1.2rem"); property("font-weight", "800"); property("color", "var(--rp-err-text)"); property("margin-top", "0.15rem") } }) {
                        Text("${totalSickSek.toLong()} kr")
                    }
                }
            }

            // Card 6: Arbetade timmar
            Div({
                style {
                    property("background", "var(--rp-surface)"); property("padding", "0.75rem 1rem")
                    property("border-radius", "12px"); property("border", "1px solid var(--rp-hl-med)")
                }
            }) {
                Span({ style { property("font-size", "0.7rem"); property("font-weight", "700"); property("color", "var(--rp-subtle)"); property("text-transform", "uppercase") } }) {
                    Text("Arbetade timmar")
                }
                Div({ style { property("font-size", "1.2rem"); property("font-weight", "800"); property("color", "var(--rp-subtle)"); property("margin-top", "0.15rem") } }) {
                    Text(minsToHours(totalWorkedMins))
                }
            }
        }

        // ── Column headers ──
        if (!isMobile) {
            Div({
                style {
                    property("display", "grid")
                    property("grid-template-columns", "minmax(220px, 1.2fr) minmax(200px, 1.5fr) 140px 190px")
                    property("gap", "0.5rem"); property("padding", "0.5rem 1.25rem")
                    property("background", "var(--rp-overlay)"); property("font-size", "0.65rem"); property("font-weight", "700")
                    property("color", "var(--rp-muted)"); property("text-transform", "uppercase"); property("letter-spacing", "0.5px")
                }
            }) {
                Span { Text("Anställd & Taxa") }
                Span { Text("Arbetade timmar & Frånvaro") }
                Span({ style { property("text-align", "right") } }) { Text("Beräknad lön") }
                Span({ style { property("text-align", "right"); property("padding-right", "0.5rem") } }) { Text("Lönebesked") }
            }
        }

        // ── Active Person rows (att utbetala) ──
        if (activeList.isEmpty()) {
            Div({ style { property("padding", "1.5rem"); property("text-align", "center"); property("color", "var(--rp-muted)"); property("font-size", "0.85rem") } }) {
                Text("Inga arbetade timmar eller löner registrerade för vald period.")
            }
        } else {
            activeList.forEachIndexed { idx, s ->
                val isLast = idx == activeList.lastIndex
                val slip = existingPayslips.find { it.userId == s.userId }
                PayrollPersonSummaryRow(
                    s = s,
                    slip = slip,
                    isLast = isLast,
                    isMobile = isMobile,
                    workplaceId = workplaceId,
                    rangeFrom = rangeFrom,
                    rangeTo = rangeTo,
                    paymentDate = customPayDate.ifBlank { rangeTo },
                    onEditSalary = { openSalaryModalForUser(s.userId) },
                    onPayslipUpdated = { refreshPayslips() }
                )
            }
        }

        // ── Footer Totals Bar ──
        Div({
            style {
                if (isMobile) {
                    property("display", "flex")
                    property("flex-direction", "column")
                    property("gap", "0.5rem")
                    property("padding", "0.75rem 1rem")
                } else {
                    property("display", "grid")
                    property("grid-template-columns", "minmax(220px, 1.2fr) minmax(200px, 1.5fr) 140px 190px")
                    property("gap", "0.5rem")
                    property("padding", "0.85rem 1.25rem")
                    property("align-items", "center")
                }
                property("background", "var(--rp-overlay)"); property("border-top", "2px solid var(--rp-info-bg)")
            }
        }) {
            Span({ style { property("font-weight", "800"); property("font-size", "0.85rem"); property("color", "var(--rp-text)") } }) {
                Text("Totalt (${activeList.size} med lön)")
            }

            Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.5rem"); property("flex-wrap", "wrap") } }) {
                HoursChip(minsToHours(totalWorkedMins), "var(--rp-pine)", "var(--rp-info-bg)", "Total arbetstid")
                if (totalVacationMins > 0) {
                    HoursChip("Semester ${minsToHours(totalVacationMins)}", "var(--rp-ok-text)", "var(--rp-ok-bg)", "Total semestertid")
                }
                if (totalSickMins > 0) {
                    HoursChip("Sjuk ${minsToHours(totalSickMins)}", "var(--rp-err-text)", "var(--rp-err-bg)", "Total sjuktid")
                }
                if (totalVabMins > 0) {
                    HoursChip("VAB ${minsToHours(totalVabMins)}", "var(--rp-subtle)", "var(--rp-overlay)", "Total VAB-tid")
                }
                if (totalLeaveMins > 0) {
                    HoursChip("Ledigt ${minsToHours(totalLeaveMins)}", "var(--rp-subtle)", "var(--rp-overlay)", "Tjänstledigt")
                }
                if (totalObSek > 0) {
                    Span({ style { property("font-size", "0.75rem"); property("font-weight", "700"); property("color", "var(--rp-warn-text)") } }) {
                        Text("Varav OB: ${totalObSek.toLong()} kr")
                    }
                }
                if (totalVacationSek > 0) {
                    Span({ style { property("font-size", "0.75rem"); property("font-weight", "700"); property("color", "var(--rp-ok-text)") } }) {
                        Text("Varav Semester: ${totalVacationSek.toLong()} kr")
                    }
                }
                if (totalSickSek > 0) {
                    Span({ style { property("font-size", "0.75rem"); property("font-weight", "700"); property("color", "var(--rp-err-text)") } }) {
                        Text("Varav Sjuklön: ${totalSickSek.toLong()} kr")
                    }
                }
            }

            Div({
                style {
                    property("display", "flex"); property("flex-direction", "column"); property("align-items", if (isMobile) "flex-start" else "flex-end")
                }
            }) {
                Div({
                    style {
                        property("background", "var(--rp-pine)"); property("color", "white")
                        property("padding", "0.4rem 0.85rem"); property("border-radius", "8px")
                        property("font-weight", "800"); property("font-size", "0.95rem")
                        property("white-space", "nowrap")
                    }
                }) { Text("${totalGrossSek.toLong()} kr brutto") }

                Span({ style { property("font-size", "0.72rem"); property("font-weight", "700"); property("color", "var(--rp-ok-text)"); property("margin-top", "0.15rem") } }) {
                    Text("Netto: ${totalNetSek.toLong()} kr")
                }
                Span({ style { property("font-size", "0.68rem"); property("font-weight", "600"); property("color", "var(--rp-subtle)") } }) {
                    Text("Kostnad: ${totalCostSek.toLong()} kr")
                }
            }

            Span { } // Empty cell for Lönebesked column in footer
        }

        // ── Inactive section (Ingen löneutbetalning denna period) ──
        if (inactiveList.isNotEmpty()) {
            Div({
                style {
                    property("border-top", "1px solid var(--rp-hl-med)")
                    property("background", "var(--rp-surface)")
                }
            }) {
                // Header toggle
                Div({
                    onClick { showInactive = !showInactive }
                    style {
                        property("display", "flex"); property("align-items", "center")
                        property("justify-content", "space-between"); property("padding", "0.75rem 1.25rem")
                        property("cursor", "pointer"); property("user-select", "none")
                    }
                }) {
                    Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.5rem") } }) {
                        FaIcon(if (showInactive) "fa-solid fa-chevron-up" else "fa-solid fa-chevron-down", size = "0.75rem", color = "var(--rp-subtle)")
                        Span({ style { property("font-size", "0.8rem"); property("font-weight", "700"); property("color", "var(--rp-subtle)") } }) {
                            Text("Ingen löneutbetalning denna period")
                        }
                        Span({ style {
                            property("background", "var(--rp-hl-med)"); property("color", "var(--rp-subtle)")
                            property("padding", "0.15rem 0.5rem"); property("border-radius", "20px")
                            property("font-size", "0.7rem"); property("font-weight", "700")
                        } }) { Text("${inactiveList.size} st") }
                    }
                    Span({ style { property("font-size", "0.72rem"); property("color", "var(--rp-muted)") } }) {
                        Text(if (showInactive) "Dölj" else "Visa lista")
                    }
                }

                // Collapsible body
                if (showInactive) {
                    Div({ style { property("border-top", "1px solid var(--rp-hl-med)") } }) {
                        inactiveList.forEachIndexed { idx, s ->
                            val isLast = idx == inactiveList.lastIndex
                            val slip = existingPayslips.find { it.userId == s.userId }
                            PayrollPersonSummaryRow(
                                s = s,
                                slip = slip,
                                isLast = isLast,
                                isDimmed = true,
                                isMobile = isMobile,
                                workplaceId = workplaceId,
                                rangeFrom = rangeFrom,
                                rangeTo = rangeTo,
                                paymentDate = customPayDate.ifBlank { rangeTo },
                                onEditSalary = { openSalaryModalForUser(s.userId) },
                                onPayslipUpdated = { refreshPayslips() }
                            )
                        }
                    }
                }
            }
        }
    }

    // Modal for setting/updating salary directly here
    editingSalaryMember?.let { member ->
        SalaryModal(
            member = member,
            onSave = { req ->
                summaryScope.launch {
                    staffRepo.updateSalary(workplaceId, member.userId, req).onSuccess {
                        editingSalaryMember = null
                        staffRepo.getStaff(workplaceId).onSuccess { staffList = it }
                        onReload()
                    }
                }
            },
            onCancel = { editingSalaryMember = null }
        )
    }
}

@Composable
private fun PayrollPersonSummaryRow(
    s: PayrollSummaryDto,
    slip: se.mittpass.shared.api.PayslipDto?,
    isLast: Boolean,
    isDimmed: Boolean = false,
    isMobile: Boolean = false,
    workplaceId: Long,
    rangeFrom: String,
    rangeTo: String,
    paymentDate: String,
    onEditSalary: () -> Unit,
    onPayslipUpdated: () -> Unit
) {
    val name = listOfNotNull(s.firstName, s.lastName).joinToString(" ").ifEmpty { s.userEmail }
    val initials = name.split(" ").mapNotNull { it.firstOrNull()?.uppercaseChar() }.take(2).joinToString("")
    val totalMin = s.normalMinutes + s.obEveningMinutes + s.obNightMinutes + s.obWeekendMinutes
    val hourlyOre = s.hourlyRateOre
    val monthlyOre = s.monthlySalaryOre
    val hasSalary = hourlyOre != null || monthlyOre != null
    val colorScheme = getEmployeeColorScheme(null, s.userId)

    val rateLabel = when {
        hourlyOre != null -> {
            val r = if (hourlyOre % 100 == 0) "${hourlyOre / 100}" else "${hourlyOre / 100.0}"
            "$r kr/h"
        }
        monthlyOre != null -> "${monthlyOre / 100} kr/mån"
        else -> null
    }

    var generatingSingle by remember { mutableStateOf(false) }
    var publishingSingle by remember { mutableStateOf(false) }

    Div({
        style {
            if (isMobile) {
                property("display", "flex")
                property("flex-direction", "column")
                property("gap", "0.6rem")
                property("padding", "0.85rem 1rem")
            } else {
                property("display", "grid")
                property("grid-template-columns", "minmax(220px, 1.2fr) minmax(200px, 1.5fr) 140px 190px")
                property("gap", "0.5rem")
                property("padding", "0.75rem 1.25rem")
                property("align-items", "center")
            }
            if (!isLast) property("border-bottom", "1px solid var(--rp-hl-med)")
            if (isDimmed) property("opacity", "0.75")
        }
    }) {

        // Name cell with designated employee color avatar & clickable salary edit
        Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.75rem") } }) {
            Div({ style {
                property("width", "32px"); property("height", "32px"); property("border-radius", "50%")
                property("background", if (isDimmed) "var(--rp-overlay)" else colorScheme.bg); property("display", "flex"); property("flex-shrink", "0")
                property("align-items", "center"); property("justify-content", "center")
                property("font-weight", "800"); property("font-size", "0.72rem")
                property("color", if (isDimmed) "var(--rp-subtle)" else colorScheme.text)
                property("border", if (isDimmed) "1px solid var(--rp-hl-med)" else "2px solid ${colorScheme.primary}")
            } }) { Text(initials.ifEmpty { "?" }) }

            Div({ style { property("display", "flex"); property("flex-direction", "column"); property("min-width", "0") } }) {
                Span({ style {
                    property("font-weight", "700"); property("font-size", "0.85rem"); property("color", "var(--rp-text)")
                    property("white-space", "nowrap"); property("overflow", "hidden"); property("text-overflow", "ellipsis")
                } }) { Text(name) }

                if (rateLabel != null) {
                    Span({
                        onClick { onEditSalary() }
                        attr("title", "Klicka för att ändra lön/OB för $name")
                        style {
                            property("font-size", "0.7rem"); property("color", "var(--rp-subtle)"); property("font-weight", "600")
                            property("cursor", "pointer"); property("text-decoration", "underline dotted")
                        }
                    }) {
                        Text(rateLabel)
                    }
                } else {
                    Button({
                        onClick { onEditSalary() }
                        attr("title", "Klicka för att sätta lön direkt")
                        style {
                            property("font-size", "0.7rem"); property("color", "var(--rp-warn-text)"); property("font-weight", "700")
                            property("background", "var(--rp-warn-bg)"); property("border", "1px solid var(--rp-warn-border)")
                            property("padding", "0.1rem 0.4rem"); property("border-radius", "6px")
                            property("cursor", "pointer"); property("margin-top", "0.15rem")
                            property("display", "inline-flex"); property("align-items", "center"); property("gap", "0.25rem")
                        }
                    }) {
                        FaIcon("fa-solid fa-plus", size = "0.6rem", color = "var(--rp-warn-text)")
                        Text("Sätt lön")
                    }
                }
            }
        }

        // Hours & Absence cell
        Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.4rem"); property("flex-wrap", "wrap") } }) {
            if (totalMin > 0) {
                HoursChip(minsToHours(totalMin), "var(--rp-pine)", "var(--rp-info-bg)", "Totalt arbetat")

                if (s.normalMinutes > 0 && (s.obEveningMinutes > 0 || s.obNightMinutes > 0 || s.obWeekendMinutes > 0)) {
                    HoursChip("Dag ${minsToHours(s.normalMinutes)}", "var(--rp-subtle)", "var(--rp-overlay)", "Normal tid")
                }

                if (s.obEveningMinutes > 0)
                    HoursChip("Kväll ${minsToHours(s.obEveningMinutes)}", "var(--rp-warn-text)", "var(--rp-warn-bg)", "Kvälls-OB")
                if (s.obNightMinutes > 0)
                    HoursChip("Natt ${minsToHours(s.obNightMinutes)}", "var(--rp-iris)", "var(--rp-info-bg)", "Natt-OB")
                if (s.obWeekendMinutes > 0)
                    HoursChip("Helg ${minsToHours(s.obWeekendMinutes)}", "var(--rp-warn-text)", "var(--rp-warn-bg)", "Helg-OB")
                if (s.overtimeMinutes > 0)
                    HoursChip("Övertid ${minsToHours(s.overtimeMinutes)}", "var(--rp-err-text)", "var(--rp-err-bg)", "Övertid")
            } else if (s.vacationMinutes == 0 && s.sickMinutes == 0 && s.vabMinutes == 0 && s.leaveMinutes == 0) {
                Span({ style { property("font-size", "0.75rem"); property("color", "var(--rp-muted)") } }) {
                    Text("0 timmar")
                }
            }

            if (s.vacationMinutes > 0) {
                val remStr = if (s.vacationDaysRemaining % 1.0 == 0.0) s.vacationDaysRemaining.toInt().toString() else s.vacationDaysRemaining.toString()
                HoursChip("Semester ${minsToHours(s.vacationMinutes)} ($remStr dgr kvar)", "var(--rp-ok-text)", "var(--rp-ok-bg)", "Semester i perioden (${s.vacationDaysUsed} dgr uttagna i år, $remStr kvar av ${s.vacationDaysTotal})")
            }
            if (s.sickMinutes > 0) {
                HoursChip("Sjuk ${minsToHours(s.sickMinutes)}", "var(--rp-err-text)", "var(--rp-err-bg)", "Sjukfrånvaro")
            }
            if (s.vabMinutes > 0) {
                HoursChip("VAB ${minsToHours(s.vabMinutes)}", "var(--rp-subtle)", "var(--rp-overlay)", "VAB")
            }
            if (s.leaveMinutes > 0) {
                HoursChip("Ledigt ${minsToHours(s.leaveMinutes)}", "var(--rp-subtle)", "var(--rp-overlay)", "Tjänstledigt")
            }
        }

        // Calculated pay cell
        Div({
            style {
                property("display", "flex")
                property("align-items", "center")
                property("gap", "0.5rem")
                property("justify-content", if (isMobile) "flex-start" else "flex-end")
            }
        }) {
            when {
                !hasSalary -> Button({
                    onClick { onEditSalary() }
                    attr("title", "Klicka för att sätta lön för $name")
                    style {
                        property("font-size", "0.76rem"); property("color", "var(--rp-warn-text)"); property("font-weight", "700")
                        property("background", "var(--rp-warn-bg)"); property("border", "1px solid var(--rp-warn-border)")
                        property("padding", "0.25rem 0.6rem"); property("border-radius", "6px")
                        property("display", "inline-flex"); property("align-items", "center"); property("gap", "0.35rem")
                        property("cursor", "pointer")
                    }
                }) {
                    FaIcon("fa-solid fa-pen-to-square", size = "0.7rem", color = "var(--rp-warn-text)")
                    Text("Sätt löneavtal")
                }

                s.totalSek > 0 -> {
                    Div({ style { property("display", "flex"); property("flex-direction", "column"); property("align-items", if (isMobile) "flex-start" else "flex-end") } }) {
                        val breakdownParts = mutableListOf<String>()
                        if (s.basePaySek > 0) {
                            val baseLabel = if (s.hourlyRateOre != null) "${s.basePaySek.toLong()} kr" else "${s.basePaySek.toLong()} kr fast"
                            breakdownParts.add(baseLabel)
                        }
                        if (s.obSurchargeSek > 0) {
                            breakdownParts.add("${s.obSurchargeSek.toLong()} kr OB")
                        }
                        if (s.sickPaySek > 0) {
                            breakdownParts.add("${s.sickPaySek.toLong()} kr sjuklön")
                        }
                        if (s.vacationPaySek > 0) {
                            breakdownParts.add("${s.vacationPaySek.toLong()} kr semester")
                        }

                        if (breakdownParts.size > 1 || (breakdownParts.size == 1 && (s.vacationPaySek > 0 || s.sickPaySek > 0))) {
                            Span({ style { property("font-size", "0.7rem"); property("font-weight", "600"); property("color", if (s.sickPaySek > 0 && s.obSurchargeSek == 0.0) "var(--rp-err-text)" else if (s.vacationPaySek > 0 && s.obSurchargeSek == 0.0) "var(--rp-ok-text)" else "var(--rp-warn-text)") } }) {
                                Text(breakdownParts.joinToString(" + "))
                            }
                        }

                        Div({
                            style {
                                property("background", "var(--rp-overlay)"); property("color", "var(--rp-pine)")
                                property("padding", "0.3rem 0.65rem"); property("border-radius", "8px")
                                property("font-weight", "800"); property("font-size", "0.85rem")
                                property("white-space", "nowrap"); property("margin-top", "0.15rem")
                                property("border", "1px solid var(--rp-hl-med)")
                            }
                        }) { Text("${s.grossPaySek.takeIf { it > 0 }?.toLong() ?: s.totalSek.toLong()} kr brutto") }

                        if (s.netPaySek > 0) {
                            Span({ style { property("font-size", "0.7rem"); property("font-weight", "700"); property("color", "var(--rp-ok-text)"); property("margin-top", "0.1rem") } }) {
                                Text("Netto: ~${s.netPaySek.toLong()} kr")
                            }
                        }
                        if (s.totalCostSek > 0) {
                            Span({ style { property("font-size", "0.68rem"); property("color", "var(--rp-subtle)"); property("font-weight", "600") } }) {
                                Text("Kostnad: ${s.totalCostSek.toLong()} kr")
                            }
                        }
                    }
                }

                else -> {
                    Span({ style { property("font-size", "0.8rem"); property("color", "var(--rp-muted)"); property("font-weight", "600") } }) {
                        Text("0 kr")
                    }
                }
            }
        }

        // Dedicated Payslip Actions Column (Enskild generering, förhandsgranskning & godkännande)
        Div({
            style {
                property("display", "flex"); property("align-items", "center")
                property("gap", "0.4rem")
                property("justify-content", if (isMobile) "flex-start" else "flex-end")
            }
        }) {
            if (!hasSalary) {
                Span({ style { property("font-size", "0.72rem"); property("color", "var(--rp-muted)") } }) {
                    Text("Kräver löneavtal")
                }
            } else if (slip == null) {
                // No payslip generated yet -> "Skapa utkast" button
                Button({
                    onClick {
                        generatingSingle = true
                        summaryScope.launch {
                            val req = se.mittpass.shared.api.GeneratePayslipsRequest(
                                periodStart = rangeFrom,
                                periodEnd = rangeTo,
                                paymentDate = paymentDate,
                                autoPublish = false,
                                userId = s.userId
                            )
                            payrollRepo.generatePayslips(workplaceId, req).onSuccess {
                                onPayslipUpdated()
                            }
                            generatingSingle = false
                        }
                    }
                    attr("title", "Skapa utkast av lönebesked för $name")
                    style {
                        property("display", "inline-flex"); property("align-items", "center"); property("gap", "0.35rem")
                        property("padding", "0.32rem 0.65rem"); property("border", "1px solid var(--rp-hl-med)")
                        property("background", "var(--rp-surface)"); property("color", "var(--rp-pine)")
                        property("border-radius", "8px"); property("font-weight", "700"); property("font-size", "0.75rem")
                        property("cursor", "pointer"); property("font-family", "inherit")
                        property("box-shadow", "0 1px 2px rgba(0,0,0,0.04)")
                    }
                }) {
                    FaIcon("fa-solid fa-file-circle-plus", size = "0.75rem", color = "var(--rp-pine)")
                    Text(if (generatingSingle) "Skapar…" else "Skapa utkast")
                }
            } else if (slip.status == "draft") {
                // Draft exists -> Preview button + Publish button
                Button({
                    onClick {
                        val token = se.mittpass.web.authStorage.getToken() ?: ""
                        val url = "/payslips/${slip.id}/view" + if (token.isNotEmpty()) "?token=$token" else ""
                        kotlinx.browser.window.open(url, "_blank")
                    }
                    attr("title", "Öppna förhandsgranskning av utkast")
                    style {
                        property("display", "inline-flex"); property("align-items", "center"); property("gap", "0.3rem")
                        property("padding", "0.32rem 0.55rem"); property("border", "1px solid var(--rp-hl-med)")
                        property("background", "var(--rp-overlay)"); property("color", "var(--rp-pine)")
                        property("border-radius", "8px"); property("font-weight", "600"); property("font-size", "0.75rem")
                        property("cursor", "pointer")
                    }
                }) {
                    FaIcon("fa-solid fa-eye", size = "0.7rem", color = "var(--rp-subtle)")
                    Text("Granska")
                }

                Button({
                    onClick {
                        publishingSingle = true
                        summaryScope.launch {
                            payrollRepo.publishPayslip(workplaceId, slip.id).onSuccess {
                                onPayslipUpdated()
                            }
                            publishingSingle = false
                        }
                    }
                    attr("title", "Godkänn och publicera till anställd")
                    style {
                        property("display", "inline-flex"); property("align-items", "center"); property("gap", "0.3rem")
                        property("padding", "0.32rem 0.6rem"); property("border", "none")
                        property("background", "linear-gradient(135deg, var(--rp-pine), var(--rp-foam))"); property("color", "white")
                        property("border-radius", "8px"); property("font-weight", "700"); property("font-size", "0.75rem")
                        property("cursor", "pointer"); property("box-shadow", "0 1px 3px rgba(0, 184, 151, 0.2)")
                    }
                }) {
                    FaIcon("fa-solid fa-check", size = "0.7rem", color = "white")
                    Text(if (publishingSingle) "…" else "Godkänn")
                }
            } else {
                // Published -> Green badge + View PDF
                Span({
                    style {
                        property("background", "var(--rp-ok-bg)"); property("color", "var(--rp-pine)")
                        property("border", "1px solid var(--rp-ok-border)"); property("padding", "0.15rem 0.45rem")
                        property("border-radius", "6px"); property("font-size", "0.72rem"); property("font-weight", "700")
                        property("display", "inline-flex"); property("align-items", "center"); property("gap", "0.25rem")
                    }
                }) {
                    FaIcon("fa-solid fa-circle-check", size = "0.65rem", color = "var(--rp-ok-text)")
                    Text("Skickat")
                }

                Button({
                    onClick {
                        val token = se.mittpass.web.authStorage.getToken() ?: ""
                        val url = "/payslips/${slip.id}/view" + if (token.isNotEmpty()) "?token=$token" else ""
                        kotlinx.browser.window.open(url, "_blank")
                    }
                    attr("title", "Öppna och spara PDF")
                    style {
                        property("display", "inline-flex"); property("align-items", "center"); property("gap", "0.3rem")
                        property("padding", "0.32rem 0.55rem"); property("border", "1px solid var(--rp-hl-med)")
                        property("background", "var(--rp-surface)"); property("color", "var(--rp-pine)")
                        property("border-radius", "8px"); property("font-weight", "600"); property("font-size", "0.75rem")
                        property("cursor", "pointer")
                    }
                }) {
                    FaIcon("fa-solid fa-print", size = "0.7rem", color = "var(--rp-pine)")
                    Text("PDF")
                }
            }
        }
    }
}

@Composable
private fun HoursChip(label: String, color: String, bg: String, title: String) {
    Span({
        attr("title", title)
        style {
            property("font-size", "0.75rem"); property("font-weight", "700")
            property("color", color); property("background", bg)
            property("padding", "0.2rem 0.5rem"); property("border-radius", "6px")
            property("white-space", "nowrap")
        }
    }) { Text(label) }
}

private fun minsToHours(mins: Int): String {
    val h = mins / 60; val m = mins % 60
    return if (m == 0) "${h}h" else "${h}h ${m}m"
}

private fun downloadFile(filename: String, content: String, mimeType: String) {
    val blob = org.w3c.files.Blob(arrayOf(content), org.w3c.files.BlobPropertyBag(type = mimeType))
    val url = org.w3c.dom.url.URL.createObjectURL(blob)
    val a = kotlinx.browser.document.createElement("a") as org.w3c.dom.HTMLAnchorElement
    a.href = url
    a.download = filename
    a.click()
    org.w3c.dom.url.URL.revokeObjectURL(url)
}
